import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchOrders } from '../../store/slices/orderSlice';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, CircularProgress, Button, Stack
} from '@mui/material';
import { Visibility, LocalShipping } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const statusColors = {
  pending: 'warning', confirmed: 'info', processing: 'primary',
  shipped: 'secondary', delivered: 'success', cancelled: 'error'
};

const formatMoney = (amount, currency = 'USD') => {
  if (amount === undefined || amount === null) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch (_) {
    return `${currency} ${Number(amount).toLocaleString()}`;
  }
};

const CustomerOrders = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orders, loading } = useSelector(state => state.orders);

  useEffect(() => { dispatch(fetchOrders()); }, [dispatch]);

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>{t('my_orders')}</Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Order #</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => {
              const total = order.pricing?.total ?? order.totalAmount;
              const currency = order.pricing?.currency || 'USD';
              return (
                <TableRow key={order._id} hover>
                  <TableCell><strong>{order.orderNumber}</strong></TableCell>
                  <TableCell>{order.items?.length || 0} items</TableCell>
                  <TableCell><strong>{formatMoney(total, currency)}</strong></TableCell>
                  <TableCell>
                    <Chip label={order.status} size="small" color={statusColors[order.status] || 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.paymentStatus || 'unpaid'}
                      size="small"
                      color={order.paymentStatus === 'paid' ? 'success' : order.paymentStatus === 'partial' ? 'info' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        component={Link}
                        to={`/customer/orders/${order._id}`}
                      >
                        View
                      </Button>
                      {order.shipment?.shipmentNumber && (
                        <Button
                          size="small"
                          startIcon={<LocalShipping />}
                          component={Link}
                          to={`/track/${order.shipment.shipmentNumber}`}
                        >
                          Track
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {orders.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No orders yet. <Button component={Link} to="/products">Browse Products</Button>
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CustomerOrders;
